import { getApp, getApps, initializeApp } from 'firebase/app';
import {
  browserLocalPersistence,
  getAuth,
  setPersistence,
  type Auth,
  type User,
} from 'firebase/auth';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY?.trim(),
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN?.trim(),
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID?.trim(),
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET?.trim(),
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID?.trim(),
  appId: process.env.REACT_APP_FIREBASE_APP_ID?.trim(),
};

const requiredConfig = [
  firebaseConfig.apiKey,
  firebaseConfig.authDomain,
  firebaseConfig.projectId,
  firebaseConfig.appId,
];

export const firebaseEnabled = requiredConfig.every(Boolean);

let authInstance: Auth | null = null;
let persistenceReady: Promise<Auth> | null = null;

export const getFirebaseAuth = (): Auth | null => {
  if (!firebaseEnabled) return null;
  if (authInstance) return authInstance;

  const app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
  authInstance = getAuth(app);
  return authInstance;
};

export const initializeFirebaseAuth = async (): Promise<Auth | null> => {
  const auth = getFirebaseAuth();
  if (!auth) return null;
  if (!persistenceReady) {
    persistenceReady = setPersistence(auth, browserLocalPersistence).then(() => auth);
  }
  return persistenceReady;
};

export const getFirebaseAccessToken = async (forceRefresh = false): Promise<string | null> => {
  // Wait for the persistence layer before reading currentUser. A page can render
  // with a restored account just before Firebase has made the token available to
  // a separate request, which previously caused account-journal saves to leave
  // without an Authorization header.
  const auth = await initializeFirebaseAuth();
  const user: User | null = auth?.currentUser ?? null;
  return user ? user.getIdToken(forceRefresh) : null;
};

export const firebaseAuthorizationHeaders = async (forceRefresh = false): Promise<Record<string, string>> => {
  const token = await getFirebaseAccessToken(forceRefresh);
  return token ? { Authorization: `Bearer ${token}` } : {};
};
